import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { submitNewPassword } from '@/lib/auth';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DocumentHead from '@/components/DocumentHead';

export default function ResetPassword() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError(t('resetPassword.errors.missingFields'));
      return;
    }

    if (password.length < 8) {
      setError(t('resetPassword.errors.tooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      await submitNewPassword(password);
      addToast(t('resetPassword.successToast'), 'success');
      navigate('/login', { replace: true });
    } catch (err: any) {
      setError(err?.message || t('resetPassword.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-12">
      <DocumentHead title={t('resetPassword.title')} noIndex />
      <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-ink">{t('resetPassword.heading')}</h1>
          <p className="mt-2 text-ink-soft">
            {t('resetPassword.subtitle')}
          </p>
        </div>

        <div className="mt-8 rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-8">
          {error && (
            <div
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <Input
              label={t('resetPassword.passwordLabel')}
              type="password"
              autoComplete="new-password"
              required
              placeholder={t('resetPassword.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Input
              label={t('resetPassword.confirmPasswordLabel')}
              type="password"
              autoComplete="new-password"
              required
              placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <Button
              type="submit"
              isLoading={isSubmitting}
              className="w-full"
              size="lg"
            >
              {t('resetPassword.submit')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-ink-soft">
          <Link
            to="/login"
            className="font-medium text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
          >
            {t('resetPassword.backToLogin')}
          </Link>
        </p>
      </div>
    </main>
  );
}
